import { getAuthenticatedUser } from '@ctw/shared/api/auth-service/user';
import { usePractitioner } from '@ctw/shared/api/fhir/practitioner';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { QUERY_KEY_AUTHENTICATED_USER_BY_ID } from '@ctw/shared/utils/query-keys';
import {
  type AuthenticatedUserState,
  type AuthenticatedZusUser,
  buildAuthenticatedUserState,
} from '@ctw/shared/utils/zus-authenticated-user';
import { isEmpty } from 'lodash-es';
import { type PropsWithChildren, createContext, useContext, useMemo } from 'react';

const AuthenticatedUserContext = createContext<AuthenticatedUserState | null>(null);

interface AuthenticatedUserProviderProps extends PropsWithChildren {}

export const AuthenticatedUserProvider = ({ children }: AuthenticatedUserProviderProps) => {
  const { requestContext } = useCTW();
  const authenticatedUserQuery = useCtwQuery({
    queryId: QUERY_KEY_AUTHENTICATED_USER_BY_ID,
    queryKey: [requestContext.authTokenState.zusUserId],
    queryFn: async ({ telemetry, requestContext }): Promise<AuthenticatedZusUser | null> => {
      if (isEmpty(requestContext.authTokenState.zusUserId)) {
        return null;
      }

      const authenticatedUserResponse = await getAuthenticatedUser({
        env: requestContext.env,
        accessToken: requestContext.authToken,
        telemetry,
      });

      return authenticatedUserResponse
        ? {
            id: authenticatedUserResponse.data.id,
            ...authenticatedUserResponse.data.attributes,
          }
        : null;
    },
  });
  const practitionerQuery = usePractitioner({
    practitionerId: requestContext.authTokenState.practitionerId,
  });

  const state: AuthenticatedUserState = useMemo(
    () =>
      buildAuthenticatedUserState({
        authTokenState: requestContext.authTokenState,
        authenticatedUser: authenticatedUserQuery.data ?? null,
        practitioner: practitionerQuery.data ?? null,
      }),
    [requestContext, authenticatedUserQuery, practitionerQuery],
  );

  if (authenticatedUserQuery.isLoading || practitionerQuery.isLoading) {
    return <LoadingSpinner centered={true} message="Loading..." />;
  }

  return (
    <AuthenticatedUserContext.Provider value={state}>{children}</AuthenticatedUserContext.Provider>
  );
};

export const useAuthenticatedUser = () => {
  const context = useContext(AuthenticatedUserContext);

  if (!context) {
    throw new Error('useAuthenticatedUser must be used within a AuthenticatedUserProvider');
  }

  return context;
};
